import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { MainLayout } from "components/layout/MainLayout";
import { AuthLayout } from "components/layout/AuthLayout";
import MarketingLayout from "components/layout/MarketingLayout";
import { RECAPTCHA_SITE_KEY } from "constants/index";

import {
  Card,
  CardHeader,
  CardBody,
  Grid,
  GridItem,
  Input,
  Textarea,
  Select,
  Button,
  ImageEditor,
  ColorPicker,
  Checkbox,
  EmployeeProfile,
  Modal,
} from "components/ui";
import { authService } from "services/auth.service";
import { usersService } from "services/users.service";
import { companiesService } from "services/companies.service";
import { countriesService } from "services/countries.service";
import { packagesService } from "services/packages.service";
import { lookupService } from "services/lookup.service";
import { createSession } from "services/payment.service";
import { CompanyStatus, Language, Lookups } from "types/index";
import { useToast } from "hooks/useToast";
import { useFieldValidation } from "hooks/useFieldValidation";
import { useAuth } from "hooks/useAuth";
import { useLicenseStatus } from "hooks/useLicenseStatus";
import { ROUTES, DemoPackageId } from "constants/index";
import { stripDataUrl } from "../../helpers/useConvertFileToBase64";
import { Package, isDemoPackage as isDemoPackageType, getPriceByDuration, getDetailByDuration } from "types/package";
import { Option } from "@/types/option";
import {
  analyzeLogo,
  LogoIntelligenceResult,
} from "../../utils/logoIntelligence";
import { set } from "react-hook-form";
import { Label } from "@mui/icons-material";
import { Company } from "@/types/company";
import { employeesService } from "services/employees.service";
import { text } from "stream/consumers";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";

const EMAIL_REGEX = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
const DIGITS_ONLY_REGEX = /^\d+$/;
const DOMAIN_REGEX = /^@[A-Za-z0-9-]+(\.[A-Za-z0-9-]+)+$/;
const BLOCKED_DOMAINS = ["gmail", "hotmail", "outlook", "yahoo"];

const createLogoDataUrl = (base64Data: string): string => {
  if (!base64Data) return "";

  if (base64Data.startsWith("data:")) {
    return base64Data;
  }

  const format = base64Data.startsWith("/9j/") ? "image/jpeg" : "image/png";
  return `data:${format};base64,${base64Data}`;
};
export const CompanyFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const userId = searchParams.get('userId');
  const stepParam = searchParams.get('step');
  const [isIndividual, setIsIndividual] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isEdit = Boolean(id) && !location.pathname.endsWith("/view") && !location.pathname.includes("/register");
  const isAdd = location.pathname.endsWith("/new");
  const isView = location.pathname.endsWith("/view");
  const isRegister = location.pathname === ROUTES.REGISTER || location.pathname.startsWith(ROUTES.REGISTER);
  const isProfile = location.pathname.includes("/profile");

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [countries, setCountries] = useState<
    (Option & { dialCode?: string })[]
  >([]);
  const [indvPackages, setIndvPackages] = useState<Option[]>([]);
  const [packages, setPackages] = useState<Option[]>([]);
  const [allPackages, setAllPackages] = useState<Package[]>([]); // Store full package objects to access price
  const [statuses, setStatuses] = useState<Option[]>([]);
  const [showLivePreview, setShowLivePreview] = useState(true);
  const [company, setCompany] = useState(true);
  const [titles, setTitles] = useState<Option[]>([]);
  const { toast } = useToast();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalLoading, setIsModalLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 4; // Step 1: Auth, Step 2: Verification, Step 3: Payment, Step 4: Company Info
  const [isCodeRequired, setIsCodeRequired] = useState<boolean | null>(null);
  const [isFlipped, setIsFlipped] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [alreadyAuth, setAlreadyAuth] = useState(false);
  const [savedCompanyId, setSavedCompanyId] = useState<number | null>(null);
  const [existingCompanyId, setExistingCompanyId] = useState<string | null>(null);
  const [createdUserId, setCreatedUserId] = useState<string | null>(null);
  const [paymentCompleted, setPaymentCompleted] = useState(false);
  
  // Check for payment completion and companyId from URL params (when returning from payment)
  useEffect(() => {
    if (isRegister) {
      const paymentDone = searchParams.get('paymentDone');
      const companyIdParam = searchParams.get('companyId');
      if (paymentDone === 'true' && companyIdParam) {
        setPaymentCompleted(true);
        setSavedCompanyId(Number(companyIdParam));
        setCurrentStep(4);   
      }
    }
  }, [searchParams, isRegister, createdUserId]);

  const handleRegisterClick = () => { 
    setIsFlipped(true);
  };

  const handleVerifyCode = async () => {  
    var result = await authService.verifyEmailCode(authForm.email, verificationCode);
    if (result && result.success === true) {
      setShowSuccess(true);
      setIsVerified(true);
      setIsCodeRequired(false); 
      setCurrentStep(isDemoPackage(form.packageId) ? 4: 3);   
    }else{
          toast.error("Invalid verification code");
    }
  };

  const handleResendCode = async () => {
    try {
      const result = await authService.resendCode(authForm.email);
      const data = (result as any)?.data ?? result;
      if (data?.skipped) {
        toast.info(data?.Message ?? "Verification not required.");
      } else if (data?.success) {
        toast.success("Verification code sent.");
      }
    } catch (e: any) {
      toast.error(e?.message ?? "Failed to resend code.");
    }
  };
  const [modalForm, setModalForm] = useState({
    code: "",
    valueEn: "",
    valueAr: "",
  });
  const [modalErrors, setModalErrors] = useState<Record<string, string>>({});
  const handleModalChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setModalForm(() => ({
      code: value.trim().replace(" ", "_").toLocaleUpperCase(),
      valueEn: value,
      valueAr: value,
    }));
    // Clear error when user starts typing
    if (modalErrors[name]) {
      setModalErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setModalForm({ code: "", valueEn: "", valueAr: "" });
    setModalErrors({});
  };

  type SubForm = {
    email: string;
    username: string;
    password: string;
    confirmPassword: string;
  };

  const [form, setForm] = useState({
    name: "",
    address: "",
    website: "",
    domain: "",
    confirmDomain: "",
    linkedInUrl: "",
    facebookUrl: "",
    xUrl: "",
    instagramUrl: "",
    phoneCountryId: "",
    phoneNumber: "",
    logo: "",
    primaryColor: "#01878f",
    secondaryColor: "#e5e7eb",
    fontColor: "#000",
    summary: "",
    isFreelancer: false,
    packageId: "",
    showLogo: true,
    isYearly: false,
  });

  const [authForm, setAuthForm] = useState({
    name: "",
    username: "",
    phoneCountryId: "",
    phoneNumber: "",
    email: "",
    password: "",
    confirmPassword: "",
  });

  const [personalForm, setPersonalForm] = useState({
    firstName: "",
    lastName: "",
    middleName: "",
    titleId: "",
    titleLookupId: "",
    personalEmail: "",
    emailLocalPart: "",
    mobileCountryId: "",
    mobileNumber: "",
    image: "",
    isActive: true,
  });


  const { executeRecaptcha } = useGoogleReCaptcha();

  const handleFormChange = (comp: boolean) => {
    setCompany(comp);
    setPersonalForm(() => ({
      firstName: "",
      lastName: "",
      middleName: "",
      titleId: "",
      titleLookupId: "",
      personalEmail: "",
      emailLocalPart: "",
      mobileCountryId: "",
      mobileNumber: "",
      image: "",
      isActive: true,
    }));
    if (!comp) {
      setForm((prev) => ({
        ...prev,
        name: "",
        address: "",
        website: "",
        domain: "",
        confirmDomain: "",
        linkedInUrl: "",
        facebookUrl: "",
        xUrl: "",
        instagramUrl: "",
        phoneCountryId: "",
        phoneNumber: "",
        logo: "",
        primaryColor: "#01878f",
        secondaryColor: "#e5e7eb",
        fontColor: "#000",
        summary: "",
        // email: "",
        // username: "",
        // password: "",
        // confirmPassword: "",
        isFreelancer: true,
        showLogo: false,
      }));
    } else {
      setForm((prev) => ({
        name: "",
        address: "",
        website: "",
        domain: "",
        confirmDomain: "",
        linkedInUrl: "",
        facebookUrl: "",
        xUrl: "",
        instagramUrl: "",
        phoneCountryId: "",
        phoneNumber: "",
        logo: "",
        primaryColor: "#01878f",
        secondaryColor: "#e5e7eb",
        fontColor: "#000",
        summary: "",
        isFreelancer: false,
        packageId: prev.packageId || "", // Preserve packageId if it exists
        showLogo: true,
        isYearly: prev.isYearly ?? false,
      }));
    }
  };
  
  const isDemoPackage = useCallback((packageId: string | number | undefined): boolean => {
    if (!packageId || allPackages.length === 0) return false;
    const id = typeof packageId === "string" ? Number(packageId) : packageId;
    if (isNaN(id) || id === 0) return false;
    const selectedPackage = allPackages.find(
      (pkg) =>
        String(pkg.id).trim() === String(id) ||
        Number(pkg.id) === id
    );
    return selectedPackage ? isDemoPackageType(selectedPackage) : false;
  }, [allPackages]);

  const packagePrice = useMemo(() => {
    const packageId = form.packageId;
    if (!packageId || packageId === "" || allPackages.length === 0) return 0;
    const packageIdNum = Number(packageId);
    if (packageId === "0" || isNaN(packageIdNum) || packageIdNum === 0) return 0;
    const selectedPackage = allPackages.find(
      (pkg) => String(pkg.id) === String(packageId) || Number(pkg.id) === packageIdNum
    );
    if (!selectedPackage) return 0;
    return getPriceByDuration(selectedPackage, form.isYearly ?? false);
  }, [form.packageId, form.isYearly, allPackages]);

  const isIndividualAccount = form.isFreelancer;
  const requiresDomainValidation =
    !isIndividualAccount && !isEdit && !isProfile;
  const requiresAuthValidation = (!isEdit && !isProfile) || isRegister;

  const validators: any = useMemo(
    () =>
      company
        ? {
            name: (state: typeof form) =>
              !state.name?.trim() ? "First name is required" : undefined,
            address: (state: typeof form) =>
              !state.address?.trim() ? "Address is required" : undefined,
            website: (state: typeof form) =>
              !state.website?.trim() ? "website is required" : undefined,
            phoneCountryId: (state: typeof form) =>
              !state.phoneCountryId ? "Country code is required" : undefined,
            phoneNumber: (state: typeof form) => {
              if (!state.phoneNumber?.trim())
                return "Mobile number is required";
              if (!DIGITS_ONLY_REGEX.test(state.phoneNumber))
                return "Only digits allowed";
              return undefined;
            },
            packageId: (state: typeof form) => {
              if (isProfile) return undefined;
              return !state.packageId?.trim()
                ? "Package selection is required"
                : undefined;
            },
            domain: (state: typeof form) => {
              if (!requiresDomainValidation) return undefined;
              if (!state.domain?.trim()) return "Domain is required";
              if (!DOMAIN_REGEX.test(state.domain))
                return "Enter a valid domain like @company.com";
              const normalized = state.domain
                .trim()
                .replace(/^@/, "")
                .toLowerCase();
              const registrablePart = normalized.split(".")[0] ?? "";
              if (BLOCKED_DOMAINS.includes(registrablePart)) {
                return "Public email domains (gmail, hotmail, outlook, yahoo) are not allowed";
              }
              return undefined;
            },
            confirmDomain: (state: typeof form) => {
              if (!requiresDomainValidation) return undefined;
              if (!state.confirmDomain?.trim())
                return "Confirm Domain is required";
              if (state.domain !== state.confirmDomain)
                return "Domains do not match";
              return undefined;
            }
          }
        : {
            firstName: (state: typeof personalForm) =>
              !state.firstName?.trim() ? "First name is required" : undefined,
            lastName: (state: typeof personalForm) =>
              !state.lastName?.trim() ? "Last name is required" : undefined,
            personalEmail: (state: typeof personalForm) => {
              return state.personalEmail?.trim()
                ? undefined
                : "Email is required";
            },
            titleId: (state: typeof personalForm) =>
              !state.titleId ? "Title is required" : undefined,
            mobileCountryId: (state: typeof personalForm) =>
              !state.mobileCountryId ? "Country code is required" : undefined,
            mobileNumber: (state: typeof personalForm) => {
              if (!state.mobileNumber?.trim())
                return "Mobile number is required";
              if (!DIGITS_ONLY_REGEX.test(state.mobileNumber))
                return "Only digits allowed";
              return undefined;
            }
          },
    [company, requiresAuthValidation, requiresDomainValidation, isProfile]
  );

  const userValidator: any = useMemo(
    () =>({
        
            name: (state: typeof form) =>
              !state.name?.trim() ? "Name is required" : undefined,
            phoneCountryId: (state: typeof form) =>
              !state.phoneCountryId ? "Country code is required" : undefined,
            phoneNumber: (state: typeof form) => {
              if (!state.phoneNumber?.trim())
                return "Mobile number is required";
              if (!DIGITS_ONLY_REGEX.test(state.phoneNumber))
                return "Only digits allowed";
              return undefined;
            },
            username: (state: typeof authForm) => {
              if (!requiresAuthValidation) return undefined;
              return !state.username?.trim()
                ? "Username is required"
                : undefined;
            },
            email: (state: typeof authForm) => {
              if (!requiresAuthValidation) return undefined;
              if (!state.email?.trim()) return "Email is required";
              if (!EMAIL_REGEX.test(state.email)) return "Enter a valid email";
              return undefined;
            },
            password: (state: typeof authForm) => {
              if (!requiresAuthValidation) return undefined;
              if (!state.password?.trim()) return "Password is required";
              if (state.password.length < 6)
                return "Password must be at least 6 characters long";
              return undefined;
            },
            confirmPassword: (state: typeof authForm) => {
              if (!requiresAuthValidation) return undefined;
              if (!state.confirmPassword?.trim())
                return "Confirm Password is required";
              if (state.password !== state.confirmPassword)
                return "Passwords do not match";
              return undefined;
            }
      }),
    [requiresAuthValidation, requiresDomainValidation, isProfile]
  );

  const { errors, validateAll, revalidateFields, resetErrors } =
    useFieldValidation<
      | typeof form
      | typeof personalForm
      | typeof authForm
      | (typeof personalForm & SubForm)
    >(currentStep == 1 ?userValidator : validators);
    

  const [logoInsights, setLogoInsights] = useState<
    LogoIntelligenceResult | undefined
  >();
  const [isLogoAnalyzing, setIsLogoAnalyzing] = useState(false);

  const [encryptedId, setEncryptedId] = useState<string | undefined>(undefined);
  const logoAnalysisRunId = useRef(0);

  const palettePresets = useMemo(() => {
    const defaults = ["#ffffff", "#000000", "#f44336", "#4caf50", "#2196f3"];
    const aiColors = (logoInsights?.palette ?? [])
      .map((color) => color?.trim().toLowerCase())
      .filter((color): color is string => Boolean(color));

    const unique = Array.from(new Set([...aiColors, ...defaults]));
    return unique.slice(0, 10);
  }, [logoInsights]);

  const runLogoAnalysis = (imageData: string) => {
    const runId = ++logoAnalysisRunId.current;
    setIsLogoAnalyzing(true);

    analyzeLogo(imageData)
      .then((insights) => {
        if (!insights) {
          return;
        }

        if (logoAnalysisRunId.current !== runId) {
          return;
        }

        setLogoInsights(insights);
        setForm((prev) => {
          const next = { ...prev };
          const fieldsToValidate: string[] = []; 
          if (insights.companyName && insights.companyName.trim()) {
            next.name = insights.companyName.trim();
            fieldsToValidate.push("name");
          }
          if (insights.primaryColor) {
            next.primaryColor = insights.primaryColor;
          }
          if (insights.secondaryColor) {
            next.secondaryColor = insights.secondaryColor;
          }
          if (insights.fontColor) {
            next.fontColor = insights.fontColor;
          }
          if (fieldsToValidate.length) {
            revalidateFields(next, fieldsToValidate);
          }
          return next;
        });

        if (insights.companyName) {
          toast.success(
            `Company name detected: "${insights.companyName}"`,
            "AI Assists"
          );
        } else {
          toast.info(
            "We tuned your branding from the uploaded logo",
            "AI Assists"
          );
        }
      })
      .catch((err) => {
        console.error("Logo analysis failed", err);
        toast.warning(
          "We couldn't analyze that logo. Feel free to pick colors manually.",
          "AI Assists"
        );
      })
      .finally(() => {
        if (logoAnalysisRunId.current === runId) {
          setIsLogoAnalyzing(false);
        }
      });
  };

  const applySuggestedName = () => {
    if (logoInsights?.companyName) {
      setForm((prev) => {
        const next = { ...prev, name: logoInsights.companyName || prev.name };
        revalidateFields(next, ["name"]);
        return next;
      });
      toast.success("Applied AI-detected company name", "AI Assists");
    }
  };

  const renderLogoAssistant = () => (
    <div className="w-full flex flex-col items-center space-y-3">
      {isLogoAnalyzing && (
        <div className="flex items-center gap-2 text-xs text-secondary-500">
          <span
            className="w-2.5 h-2.5 rounded-full bg-primary-500 animate-ping"
            aria-hidden
          />
          <span>Analyzing your logo with AI...</span>
        </div>
      )}

      {!isLogoAnalyzing && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => form.logo && runLogoAnalysis(form.logo)}
          disabled={!form.logo}
        >
          Re-run AI styling
        </Button>
      )}

      {!isLogoAnalyzing && logoInsights?.palette?.length ? (
        <div className="flex flex-col items-center space-y-2">
          <span className="text-xs text-secondary-500 uppercase tracking-wide">
            Palette detected
          </span>
          <div className="flex items-center gap-2">
            {logoInsights.palette.slice(0, 5).map((color) => (
              <div
                key={color}
                className="w-8 h-8 rounded-md border border-secondary-200 shadow-sm"
                style={{ backgroundColor: color }}
                title={color}
              />
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );

  const openModal = () => {
    setIsModalOpen(true);
    setModalForm({ code: "", valueEn: "", valueAr: "" });
    setModalErrors({});
  };

  const handleSelectChange: React.ChangeEventHandler<HTMLSelectElement> = (
    e
  ) => {
    const { name, value } = e.target;
    if (name === "titleId") {
      const selected = titles.find((t) => String(t.value) === String(value));
      setPersonalForm((prev) => {
        const next = {
          ...prev,
          titleId: String(value),
          titleLookupId: String(selected?.value || ""),
        };
        revalidateFields(next, ["titleId"]);
        return next;
      });
      return;
    }
    setForm((prev) => {
      const next = { ...prev, [name]: value };
      revalidateFields(next, [name]);
      return next;
    });
  };

  const getDummyEmployee = () => {
    const selectedCountry = countries.find(
      (c) => c.value.toString() === form.phoneCountryId
    );
    const dialCode = selectedCountry?.dialCode || "";

    const formattedPhoneNumber =
      form.phoneNumber && dialCode
        ? `${dialCode} ${form.phoneNumber}`.trim()
        : form.phoneNumber || "";

    return {
      id: "preview-1",
      name: form.name || "Guest",
      firstName: personalForm.firstName || "Guest",
      lastName: personalForm.lastName || "",
      middleName: personalForm.middleName || "",
      title:
        titles.find(
          (t) => t.value.toString() === personalForm.titleId.toString()
        )?.label || "Senior Marketing Manager",
      email: company
        ? authForm.email || "Guest@company.com"
        : personalForm.personalEmail || "Guest@company.com",
      mobileNumber:
        personalForm.mobileNumber && personalForm.mobileCountryId
          ? countries
              .find(
                (t) =>
                  t.value.toString() === personalForm.mobileCountryId.toString()
              )
              ?.label.split(" ")[0] +
            " " +
            personalForm.mobileNumber
          : "+1 (555) 987-6543",
      isActive: true,
      image: personalForm.image || "",
      companyName: form.name || "Your Company",
      company: form.name || "Your Company",
      logo: form.logo,
      primaryColor: form.primaryColor,
      secondaryColor: form.secondaryColor,
      fontColor: form.fontColor,
      showLogo: form.showLogo,
      companyWebsite: form.website,
      companyPhone: formattedPhoneNumber,
      companyCountryCode: dialCode,
      summary: form.summary,
      isFreelancer: form.isFreelancer,
      facebookUrl: form.facebookUrl,
      instagramUrl: form.instagramUrl,
      linkedinUrl: form.linkedInUrl,
      xUrl: form.xUrl,
      companyDetails: {
        name: form.name || "Your Company",
        logo: form.logo,
        primaryColor: form.primaryColor,
        secondaryColor: form.secondaryColor,
        fontColor: form.fontColor,
        showLogo: form.showLogo,
        website: form.website,
        phoneNumber: formattedPhoneNumber,
        countryCode: dialCode,
        summary: form.summary,
        isFreelancer: form.isFreelancer,
        facebookUrl: form.facebookUrl,
        instagramUrl: form.instagramUrl,
        linkedInUrl: form.linkedInUrl,
        xUrl: form.xUrl,
      },
    };
  };

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const items = await countriesService.getAll();
        setCountries(
          (items || []).map((c: any) => ({
            value: c.id,
            label: `${c.dialCode || c.code} (${c.nameEN})`,
            dialCode: c.dialCode || c.code,
          }))
        );

        const lookupTitles = await lookupService.getByParentCode(
          Lookups.EmployeeTitles,
          Language.ENGLISH
        );
        setTitles(
          (lookupTitles || []).map((t: any) => ({
            value: (t.lookupId ??
              t.LookupId ??
              t.id ??
              t.Id ??
              t.code ??
              t.Code) as any,
            label: t.nameEN || t.NameEN || t.name || t.Name,
          }))
        );

        const packagesRes = await packagesService.list(1, 100);
        const allPackagesData = packagesRes.data || [];
        const indvPackages = allPackagesData.filter(p => p.isIndividual === true);
        const companyPackages = allPackagesData.filter(p => p.isIndividual === false);
         
        setAllPackages(allPackagesData);
        
        const validityLabelForDuration = (pkg: Package, isYearly: boolean) => {
          const detail = getDetailByDuration(pkg, isYearly);
          const days = detail?.validityDays ?? "—";
          return `${pkg.name} (${pkg.numberOfUsers} users, ${days} days)`;
        };
        const defaultIsYearly = false;
        setPackages(
          (companyPackages || []).map((pkg: Package) => ({
            value: pkg.id,
            label: validityLabelForDuration(pkg, defaultIsYearly),
          }))
        );
        setIndvPackages(
          (indvPackages || []).map((pkg: Package) => ({
            value: pkg.id,
            label: validityLabelForDuration(pkg, defaultIsYearly),
          }))
        );
        const exists = indvPackages.some(p => String(p.id) === String(id));
        setIsIndividual(exists);
        const statusLookups = await lookupService.getByParentCode(
          Lookups.CompanyStatus,
          Language.ENGLISH
        );
        setStatuses(
          (statusLookups || []).map((s: any) => ({
            value: (s.lookupId ??
              s.LookupId ??
              s.id ??
              s.Id ??
              s.code ??
              s.Code) as any,
            label: s.nameEN || s.NameEN || s.name || s.Name,
          }))
        );

        if ((isEdit || isView || isProfile) && id) {
          const comp = await companiesService.getById(id);
          setEncryptedId((comp as any).encryptedId);
          setForm({
            name: (comp as any).name || "",
            address: (comp as any).address || "",
            website: (comp as any).website || "",
            domain: String((comp as any).domain || ""),
            confirmDomain: "",
            facebookUrl: String((comp as any).facebookUrl || ""),
            instagramUrl: String((comp as any).instagramUrl || ""),
            xUrl: String((comp as any).xUrl || ""),
            linkedInUrl: String((comp as any).linkedInUrl || ""),
            phoneCountryId: String((comp as any).phoneCountryId || ""),
            phoneNumber: String((comp as any).phoneNumber || ""),
            logo: createLogoDataUrl(comp.logo || ""),
            fontColor: (comp as any).fontColor || "#000",
            primaryColor: (comp as any).primaryColor || "#01878f",
            secondaryColor: (comp as any).secondaryColor || "#e5e7eb",
            summary: String((comp as any).summary || ""),
            // email: (comp as any).email || "",
            // username: (comp as any).username || "",
            // password: (comp as any).password || "",
            // confirmPassword: (comp as any).confirmPassword || "",
            isFreelancer: Boolean((comp as any).isFreelancer || false),
            packageId: String((comp as any).packageId || ""),
            showLogo: (comp as any).showLogo ?? true,
            isYearly: Boolean((comp as any).isYearly ?? false),
          });
          resetErrors();
        } else if (isAdd || isRegister) {
          // Only set default packageId if we don't have userId (meaning no user data loaded yet)
          // If userId exists, the second useEffect will handle setting packageId from user data
          if (!userId) {
            setForm((prev) => {
              // Only set packageId if it's not already set in form
              if (!prev.packageId) {
                let packageIdToUse: string = String(prev.isFreelancer ? DemoPackageId.Individual : DemoPackageId.Company);
                let isYearlyFromUrl = searchParams.get("isYearly") === "true";
                // Priority: 1. packageDetailsId from URL (from /register?packageDetailsId=), 2. URL id (from /register/:id), 3. Default
                const urlPackageDetailsId = searchParams.get("packageDetailsId");
                if (urlPackageDetailsId && allPackagesData.length > 0) {
                  let found = false;
                  for (const pkg of allPackagesData) {
                    const details = (pkg as Package).details;
                    const detail = Array.isArray(details)
                      ? details.find((d: any) => String(d.id) === String(urlPackageDetailsId))
                      : undefined;
                    if (detail) {
                      packageIdToUse = String((pkg as Package).id);
                      isYearlyFromUrl =
                        Number((detail as any).durationId) === 21 ||
                        (detail as any).duration === "Yearly";
                      setCompany(!(pkg as Package).isIndividual);
                      setIsIndividual((pkg as Package).isIndividual);
                      found = true;
                      break;
                    }
                  }
                  if (!found) {
                    packageIdToUse = String(prev.isFreelancer ? DemoPackageId.Individual : DemoPackageId.Company);
                  }
                } else if (id && isRegister) {
                  packageIdToUse = String(id);
                  const selectedPackage = allPackagesData.find(p => String(p.id) === String(id));
                  if (selectedPackage) {
                    setCompany(!selectedPackage.isIndividual);
                    setIsIndividual(selectedPackage.isIndividual);
                  }
                } else {
                  packageIdToUse = String(prev.isFreelancer ? DemoPackageId.Individual : DemoPackageId.Company);
                }
                const next = { ...prev, packageId: packageIdToUse, isYearly: isYearlyFromUrl };
                revalidateFields(next, ["packageId"]);
                return next;
              }
              return prev;
            });
          }
        }
        // Only sync company from isIndividual when not in register flow (userId); register flow sets both from package in loadUserAndCompanyData
        if (!userId) setCompany(!isIndividual);
      } catch (e: any) {
        const friendly =
          "An error occurred on the server. Please contact the administrator.";
        setError(e.message ?? friendly);
        toast.error(e.message ?? friendly);
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [id, isEdit, isView, isProfile, resetErrors, revalidateFields, userId, searchParams]);

  // Rebuild package option labels when selected duration (isYearly) or packages change, so labels show the selected detail's validity
  useEffect(() => {
    if (allPackages.length === 0) return;
    const companyPackagesList = allPackages.filter((p: Package) => !p.isIndividual);
    const indvPackagesList = allPackages.filter((p: Package) => !!p.isIndividual);
    const labelForDetail = (pkg: Package, isYearly: boolean) => {
      const detail = getDetailByDuration(pkg, isYearly);
      const days = detail?.validityDays ?? "—";
      return `${pkg.name} (${pkg.numberOfUsers} users, ${days} days)`;
    };
    const isYearly = form.isYearly ?? false;
    setPackages(companyPackagesList.map((pkg: Package) => ({ value: pkg.id, label: labelForDetail(pkg, isYearly) })));
    setIndvPackages(indvPackagesList.map((pkg: Package) => ({ value: pkg.id, label: labelForDetail(pkg, isYearly) })));
  }, [allPackages, form.isYearly]);

  useEffect(() => {
      const loadUserAndCompanyData = async () => { 
        if (userId) {
          try {
            setIsLoading(true);
             
            const { user: userData, company: companyData } = await usersService.getRegisterData(userId);
             
            setIsCodeRequired((userData as any).isCodeRequired ?? false);
             
            const targetStep = stepParam ? parseInt(stepParam) : 4;
            setCurrentStep(targetStep);
            setAlreadyAuth(true);
            setIsVerified(!((userData as any).isCodeRequired ?? false));
            setShowSuccess(!((userData as any).isCodeRequired ?? false));
            
            // If step is 3 (payment), mark payment as not completed yet
            if (targetStep === 3) {
              setPaymentCompleted(false);
            }
            
            // Ensure packages are loaded first so we can resolve packageDetailsId -> packageId + isYearly
            let packagesForLookup = allPackages;
            if (allPackages.length === 0) {
              try {
                const packagesRes = await packagesService.list(1, 100);
                const allPackagesData = packagesRes.data || [];
                setAllPackages(allPackagesData);
                packagesForLookup = allPackagesData;
              } catch (e) {
                console.error("Failed to load packages:", e);
              }
            }
            
            // Resolve package: API now returns packageDetailsId; derive packageId and isYearly for form/price
            const packageDetailsId = (userData as any)?.packageDetailsId ?? (companyData as any)?.packageDetailsId;
            let userPackageId: string | number | undefined = (userData as any)?.packageId;
            let userIsYearly: boolean | undefined = (userData as any)?.isYearly;
            if (packageDetailsId != null && packagesForLookup.length > 0) {
              for (const pkg of packagesForLookup) {
                const details = (pkg as any).details;
                const detail = Array.isArray(details)
                  ? details.find((d: any) => String(d.id) === String(packageDetailsId))
                  : undefined;
                if (detail) {
                  userPackageId = (pkg as any).id;
                  userIsYearly =
                    Number((detail as any).durationId) === 21 ||
                    (detail as any).duration === "Yearly";
                  break;
                }
              }
            }
            
            setAuthForm((prev) => ({
              ...prev,
              email: userData.email || "",
              username: userData.username || "",
              name: userData.name || "",
              phoneCountryId: userData.phoneId ? String(userData.phoneId) : "", 
              phoneNumber: userData.phone || "",
            }));

            if (companyData) {
              // Store the existing company_id for update logic
              setExistingCompanyId(String(companyData.id));
              
              setForm((prev) => {
                // Preserve packageId from user if company doesn't have it, or use company's packageId if it exists
                const finalPackageId = companyData.packageId 
                  ? String(companyData.packageId) 
                  : (userPackageId ? String(userPackageId) : prev.packageId);
                
                return {
                  ...prev,
                  name: companyData.name || prev.name,
                  address: companyData.address || prev.address,
                  website: companyData.website || prev.website,
                  domain: String(companyData.domain || prev.domain),
                  confirmDomain:  String(companyData.domain || prev.confirmDomain),
                  facebookUrl: String(companyData.facebookUrl || prev.facebookUrl),
                  instagramUrl: String(companyData.instagramUrl || prev.instagramUrl),
                  xUrl: String(companyData.xUrl || prev.xUrl),
                  linkedInUrl: String(companyData.linkedInUrl || prev.linkedInUrl),
                  phoneCountryId: String(companyData.phoneCountryId || prev.phoneCountryId),
                  phoneNumber: String(companyData.phoneNumber || prev.phoneNumber),
                  logo: createLogoDataUrl(companyData.logo || ""),
                  fontColor: companyData.fontColor || prev.fontColor,
                  primaryColor: companyData.primaryColor || prev.primaryColor,
                  secondaryColor: companyData.secondaryColor || prev.secondaryColor,
                  summary: String(companyData.summary || prev.summary),
                  isFreelancer: Boolean(companyData.isFreelancer || false),
                  packageId: finalPackageId || prev.packageId, // Ensure we never set empty string
                  showLogo: companyData.showLogo ?? prev.showLogo,
                  isYearly: userIsYearly ?? prev.isYearly,
                };
              });
              setCompany(!Boolean(companyData.isFreelancer));
            } else if (userPackageId !== undefined && userPackageId !== "") {
              // If no company data but user has packageId (or resolved from packageDetailsId), set it
              setForm(prev => {
                const newPackageId = String(userPackageId);
                const isYearlyFromApi = userIsYearly ?? prev.isYearly;
                if (prev.packageId !== newPackageId || prev.isYearly !== isYearlyFromApi) {
                  return { ...prev, packageId: newPackageId, isYearly: isYearlyFromApi };
                }
                return prev;
              });
            } else {
              // No company and no packageId; still set isYearly from API response
              setForm(prev => {
                const isYearlyFromApi = userIsYearly ?? prev.isYearly;
                return prev.isYearly !== isYearlyFromApi ? { ...prev, isYearly: isYearlyFromApi } : prev;
              });
            }
            
            // When no company data but we have packageId (e.g. step 4 from account), set company vs individual from package
            if (!companyData && userPackageId && packagesForLookup.length > 0) {
              const selectedPackage = packagesForLookup.find(
                (p: { id: string | number }) => String(p.id) === String(userPackageId)
              );
              if (selectedPackage && typeof (selectedPackage as any).isIndividual === "boolean") {
                setCompany(!(selectedPackage as any).isIndividual);
                setIsIndividual((selectedPackage as any).isIndividual);
              }
            }
          } catch (error: any) {
            console.error("Failed to load register data:", error);
            toast.error(error?.message || "Could not load user data. Please try again.");
          } finally {
            setIsLoading(false);
          }
        }
      };
      loadUserAndCompanyData();
}, [userId, stepParam])

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, [currentStep]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    const checked = "checked" in e.target ? e.target.checked : false;
    if (
      company ||
      name.toLocaleLowerCase().includes("url") ||
      name.toLocaleLowerCase() === "summary" ||
      name.toLocaleLowerCase() === "email" ||
      // name.toLocaleLowerCase() === "username" ||
      // name.toLocaleLowerCase() === "password" ||
      // name.toLocaleLowerCase() === "confirmpassword" ||
      name.toLocaleLowerCase() === "website"
    ) {
      setForm((prev) => {
        const next = {
          ...prev,
          [name]: type === "checkbox" ? checked : value,
        } as typeof prev;
        const fieldsToValidate = new Set<string>([name]);
        if (name === "isFreelancer" && (isAdd || isRegister)) {
          next.packageId = String(
            checked ? DemoPackageId.Individual : DemoPackageId.Company
          );
          fieldsToValidate.add("packageId");
          fieldsToValidate.add("domain");
          fieldsToValidate.add("confirmDomain");
          fieldsToValidate.add("email");
        }
        if (name === "domain" || name === "confirmDomain") {
          fieldsToValidate.add("domain");
          fieldsToValidate.add("confirmDomain");
        }
        // if (name === "password" || name === "confirmPassword") {
        //   fieldsToValidate.add("password");
        //   fieldsToValidate.add("confirmPassword");
        // }
        revalidateFields(next, Array.from(fieldsToValidate));
        return next;
      });
    } else {
      setPersonalForm((prev) => {
        const next = {
          ...prev,
          [name]: value,
        };
        // const targetFields = name === "emailLocalPart" ? ["email"] : [name];
        // revalidateFields(next, targetFields);
        return next;
      });
    }
  };

  const handleColorChange =
    (field: "primaryColor" | "secondaryColor" | "fontColor") =>
    (color: string) => {
      setForm((prev) => ({ ...prev, [field]: color }));
    };

  const handleCountryChange: React.ChangeEventHandler<HTMLSelectElement> = (
    e
  ) => {
    if (company)
      setForm((prev) => {
        const next = { ...prev, phoneCountryId: String(e.target.value) };
        revalidateFields(next, ["phoneCountryId"]);
        return next;
      });
    else
      setPersonalForm((prev) => {
        const next = { ...prev, mobileCountryId: String(e.target.value) };
        revalidateFields(next, ["phoneCountryId"]);
        return next;
      });
  };

  const handlePackageChange: React.ChangeEventHandler<HTMLSelectElement> = (
    e
  ) => {
    const newPackageId = String(e.target.value);
    setForm((prev) => {
      const next = { ...prev, packageId: newPackageId };
      revalidateFields(next, ["packageId"]);
      return next;
    });
    // Keep company/individual in sync with selected package
    const selectedPackage = allPackages.find(
      (p: { id: string | number }) => String(p.id) === newPackageId
    );
    if (selectedPackage && typeof (selectedPackage as any).isIndividual === "boolean") {
      setCompany(!(selectedPackage as any).isIndividual);
      setIsIndividual((selectedPackage as any).isIndividual);
    }
  };
  const handleMobileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digitsOnly = e.target.value.replace(/\D+/g, "");
    if (company)
      setForm((prev) => {
        const next = { ...prev, phoneNumber: digitsOnly };
        revalidateFields(next, ["phoneNumber"]);
        return next;
      });
    else
      setPersonalForm((prev) => {
        const next = { ...prev, mobileNumber: digitsOnly };
        revalidateFields(next, ["mobileNumber"]);
        return next;
      });
  };

  const handleImageChange = (imageData: string | null) => {
    if (company) setForm((prev) => ({ ...prev, logo: imageData || "" }));
    else setPersonalForm((prev) => ({ ...prev, image: imageData || "" }));
    setLogoInsights(undefined);

    if (!imageData) {
      setIsLogoAnalyzing(false);
      return;
    }

    runLogoAnalysis(imageData);
  };

  const onSubmit = async () => {
    setIsLoading(true);
    setError(null);
    try {
      if (!company) {
        var validatePerson = validateAll(personalForm);
        if (Object.keys(validatePerson).length > 0) {
          setIsLoading(false);
          return;
        }
      } else {
        const validationResult = validateAll(form);
        if (Object.keys(validationResult).length > 0) {
          setIsLoading(false);
          return;
        }
      }

      const imageBase64 =
        typeof form.logo === "string" && form.logo.startsWith("data:")
          ? stripDataUrl(form.logo)
          : form.logo || undefined;

      const profileImageBase64 =
        typeof personalForm.image === "string" &&
        personalForm.image.startsWith("data:")
          ? stripDataUrl(personalForm.image)
          : personalForm.image || undefined;

      const payload = {
        name: company
          ? form.name
          : `${personalForm.firstName} ${personalForm.lastName}`,
        address: form.address,
        website: form.website,
        domain:
          !isEdit && !isProfile && !form.isFreelancer ? form.domain : undefined,
        linkedInUrl: form.linkedInUrl,
        facebookUrl: form.facebookUrl,
        xUrl: form.xUrl,
        instagramUrl: form.instagramUrl,
        primaryColor: form.primaryColor || "#01878f",
        secondaryColor: form.secondaryColor || "#e5e7eb",
        fontColor: form.fontColor || "#000",
        summary: form.summary,
        showLogo: form.showLogo,
        phoneCountryId: company
          ? form.phoneCountryId
            ? Number(form.phoneCountryId)
            : undefined
          : personalForm.mobileCountryId,
        phoneNumber: company
          ? form.phoneNumber
            ? Number(form.phoneNumber)
            : undefined
          : personalForm.mobileNumber,
        logo: imageBase64,
        isFreelancer: form.isFreelancer,
        packageId: form.packageId || undefined,
        username:
          (!isEdit && !isProfile) || isRegister ? authForm.username : undefined,
        email: (!isEdit && !isProfile) || isRegister ? authForm.email : undefined,
        password:
          (!isEdit && !isProfile) || isRegister ? authForm.password : undefined,
        id: (isRegister && existingCompanyId) ? Number(existingCompanyId) : (isEdit || isProfile ? Number(id || 0) : undefined),
        companyId: (isRegister && existingCompanyId) ? Number(existingCompanyId) : undefined,
      } as any;

      const personalInfoData = {
        firstName: personalForm.firstName,
        lastName: personalForm.lastName,
        middleName: personalForm.middleName,
        titleId: Number(personalForm.titleId),
        titleLookupId: Number(personalForm.titleLookupId),
        email: personalForm.personalEmail,
        emailLocalPart: "",
        mobileCountryId: Number(personalForm.mobileCountryId),
        mobileNumber: personalForm.mobileNumber,
        image: profileImageBase64,
        isActive: true,
      };
      if (isEdit && id) {
        await companiesService.update(id, payload);
        toast.success("Company updated successfully");
      } else if (isProfile && id) {
        await companiesService.update(id, payload);
        toast.success("Company profile updated successfully");
      } else if (isRegister) {  
        const companyId = existingCompanyId ? Number(existingCompanyId) : 0;
        
        if (company) {
          if (companyId > 0) { 
            await companiesService.update(existingCompanyId!, payload);
            toast.success("Company updated successfully");
          } else { 
            await authService.register(payload);
            toast.success("Company registered successfully");
          }
        } else {
          await authService.registerIndividual({
            company: payload,
            employee: personalInfoData,
          });
          toast.success("Profile created successfully");
        }
        navigate(ROUTES.LOGIN);
      } else {
        const created = await companiesService.create(payload);
        setSavedCompanyId((created as any).id);
        toast.success("Company created successfully");
      }
      if (!isRegister) {
        if (isProfile) {
          navigate(ROUTES.DASHBOARD);
        } else {
          navigate(ROUTES.COMPANIES);
        }
      }
    } catch (e: any) {
      const friendly =
        "An error occurred on the server. Please contact the administrator.";
      setError(e.message ?? friendly);
      toast.error(e.message ?? friendly);
    } finally {
      setIsLoading(false);
    }
  };

  const loadCheckoutScript = () => {
    return new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src =
        "https://creditlibanais-netcommerce.gateway.mastercard.com/static/checkout/checkout.min.js";
      script.onload = resolve;
      script.onerror = reject;
      document.body.appendChild(script);
    });
  };

  const onPay = async () => {
    try {
      await loadCheckoutScript();

      // In registration flow we use account id (createdUserId or userId from URL) for payment
      let userIdToUse = 0;
      let accountIdToUse: number | undefined;
      if (isRegister) {
        if (createdUserId) {
          accountIdToUse = Number(createdUserId);
        } else if (userId) {
          accountIdToUse = Number(userId);
        } else {
          toast.error("Please complete step 1 first.");
          return;
        }
      } else if (id && (isEdit || isProfile)) {
        toast.error("Payment is only available during registration");
        return;
      } else {
        toast.error("User ID is required for payment");
        return;
      }
      const selectedPackage = allPackages.find(
        (pkg) => String(pkg.id) === form.packageId
      );
      const amount = selectedPackage
        ? getPriceByDuration(selectedPackage, form.isYearly ?? false)
        : 0;
      if (amount <= 0 && selectedPackage && !isDemoPackageType(selectedPackage)) {
        toast.error("Selected package has no price for this billing period");
        return;
      }

      const detail = selectedPackage ? getDetailByDuration(selectedPackage, form.isYearly ?? false) : undefined;
      const res = await createSession({
        amount: amount || 0,
        userId: userIdToUse,
        accountId: accountIdToUse,
        companyId: null,
        packageDetailsId: detail ? Number(detail.id) : undefined,
      });

      let sessionData: any;
       
      const resAny = res as any;
       
      if (resAny && typeof resAny === 'object') {
        if (resAny.data !== undefined) { 
          // Wrapped in ApiResponse structure
          const responseData = resAny.data;
          if (typeof responseData === 'string') {
            try {
              sessionData = JSON.parse(responseData);
            } catch (e) {
              sessionData = responseData;
            }
          } else {
            sessionData = responseData;
          }
        } else if (resAny.session) { 
          // The response itself is the session data
          sessionData = resAny;
        } else {
          sessionData = resAny;
        }
      } else {
        sessionData = resAny;
      }

      const sessionId = sessionData?.session?.id;

      if (!sessionId) {
        console.error("Session ID not found in response:", { sessionData, res });
        toast.error("Failed to get payment session");
        return;
      }

      localStorage.setItem("mpgs_session_id", sessionId);

      (window as any).Checkout.configure({
        session: { id: sessionId },
      });

      (window as any).Checkout.showPaymentPage();
    } catch (e: any) {
      toast.error(e?.message || "Failed to initialize payment");
    }
  };

  const handleNext = async () => {
    if (currentStep === 1 && isVerified == false) {
    setIsLoading(true);
    setError(null);
    var validatePerson = validateAll(authForm);
    if (Object.keys(validatePerson).length > 0) {
      setIsLoading(false);
      return;
    }

    // Execute reCAPTCHA v3
    if (!executeRecaptcha) {
      toast.error("reCAPTCHA is not loaded. Please try again.");
      setIsLoading(false);
      return;
    }

    try {
      const token = await executeRecaptcha("register_form");
      const pkg = form.packageId && allPackages.length > 0
        ? allPackages.find((p) => String(p.id) === String(form.packageId))
        : undefined;
      const detail = pkg ? getDetailByDuration(pkg, form.isYearly ?? false) : undefined;
      const payload = {
        name: authForm.name,
        username: authForm.username,
        phoneCountryId: authForm.phoneCountryId,
        phoneNumber: authForm.phoneNumber,
        email: authForm.email,
        password: authForm.password,
        captchaToken: token,
        packageDetailsId: detail ? Number(detail.id) : undefined,
      };
      const userResult = await usersService.createUser(payload);
      toast.success("User created successfully"); 
      setIsCodeRequired((userResult as any)?.isCodeRequired ?? true); 
      if (userResult && (userResult as any).id) {
        setCreatedUserId(String((userResult as any).id));
      } 
      const userPackageId = (userResult as any)?.packageId || form.packageId; 
      setCurrentStep(currentStep + 1); 
    } catch(e: any){
      toast.error(e.message ?? "Failed to create user");
    }
    setIsLoading(false);
    } else if (currentStep === 3 && isRegister) { 
      setIsLoading(true);
      setError(null);
      // When individual/personal, validate personal form only; when company, validate company form
      var validationErrors = company ? validateAll(form) : validateAll(personalForm);
      if (Object.keys(validationErrors).length > 0) {
        setIsLoading(false);
        return;
      }

      try {
        const imageBase64 = stripDataUrl(form.logo || "");
        const profileImageBase64 = stripDataUrl(personalForm.image || "");

        const payload = {
          id: existingCompanyId ? Number(existingCompanyId) : (savedCompanyId ? savedCompanyId : 0),  
          name: form.name,
          address: form.address,
          website: form.website,
          domain: form.domain,
          linkedInUrl: form.linkedInUrl,
          facebookUrl: form.facebookUrl,
          xUrl: form.xUrl,
          instagramUrl: form.instagramUrl,
          phoneCountryId: company
            ? form.phoneCountryId
              ? Number(form.phoneCountryId)
              : undefined
            : personalForm.mobileCountryId,
          phoneNumber: company
            ? form.phoneNumber
              ? Number(form.phoneNumber)
              : undefined
            : personalForm.mobileNumber,
          logo: imageBase64,
          isFreelancer: form.isFreelancer,
          packageId: form.packageId ? Number(form.packageId) : undefined,
          packageDetailsId: (() => {
            if (!form.packageId || allPackages.length === 0) return undefined;
            const pkg = allPackages.find((p) => String(p.id) === String(form.packageId));
            const detail = pkg ? getDetailByDuration(pkg, form.isYearly ?? false) : undefined;
            return detail ? Number(detail.id) : undefined;
          })(),
          username: authForm.username,
          email: authForm.email,
          password: authForm.password,
        } as any;

        const personalInfoData = {
          firstName: personalForm.firstName,
          lastName: personalForm.lastName,
          middleName: personalForm.middleName,
          titleId: Number(personalForm.titleId),
          titleLookupId: Number(personalForm.titleLookupId),
          email: personalForm.personalEmail,
          emailLocalPart: "",
          mobileCountryId: Number(personalForm.mobileCountryId),
          mobileNumber: personalForm.mobileNumber,
          image: profileImageBase64,
          isActive: true,
        };

        if (company) {
          const result = await authService.register(payload); 
          const companyId = (result as any)?.id || (result as any)?.companyId || (result as any)?.data?.id;
          if (companyId) {
            setSavedCompanyId(Number(companyId));
          } else {
            console.error("Company ID not found in registration response", result);
            toast.error("Company created but ID not found. Please contact support.");
            return;
          }
        } else {
          const result = await authService.registerIndividual({
            company: payload,
            employee: personalInfoData,
          }); 
          const companyId = (result as any)?.id || (result as any)?.companyId || (result as any)?.data?.id;
          if (companyId) {
            setSavedCompanyId(Number(companyId));
          } else {
            console.error("Company ID not found in registration response", result);
            toast.error("Company created but ID not found. Please contact support.");
            return;
          }
        }
        
        toast.success("Company information saved");
        setCurrentStep(currentStep + 1);
      } catch (e: any) {
        const friendly =
          "An error occurred on the server. Please contact the administrator.";
        setError(e.message ?? friendly);
        toast.error(e.message ?? friendly);
      } finally {
        setIsLoading(false);
      }
    } else {
      setCurrentStep(currentStep + 1);
    }
  };

  const saveNewLookup = async () => {
    setIsModalLoading(true);
    setModalErrors({});

    try {
      const nextErrors: Record<string, string> = {};

      if (!modalForm.code?.trim()) nextErrors.code = "Code is required";
      if (!modalForm.valueEn?.trim())
        nextErrors.valueEn = "English value is required";
      if (!modalForm.valueAr?.trim())
        nextErrors.valueAr = "Arabic value is required";

      setModalErrors(nextErrors);

      if (Object.keys(nextErrors).length > 0) {
        setIsModalLoading(false);
        return;
      }

      // Get the parent lookup ID for EmployeeTitles
      const parentLookups = await lookupService.getAll();
      const employeeTitlesParent = parentLookups.find(
        (l: any) => l.code === Lookups.EmployeeTitles
      );

      if (!employeeTitlesParent) {
        throw new Error("Employee Titles parent lookup not found");
      }

      const lookupDetails = [
        {
          id: 0,
          name: modalForm.valueEn,
          languageId: 1,
        },
        {
          id: 0,
          name: modalForm.valueAr,
          languageId: 2,
        },
      ];

      const payload = {
        code: modalForm.code,
        valueEn: modalForm.valueEn,
        valueAr: modalForm.valueAr,
        parentId: employeeTitlesParent.id,
        lookupDetails,
      } as any;

      await lookupService.create(payload);

      const lookupTitles = await lookupService.getByParentCode(
        Lookups.EmployeeTitles,
        Language.ENGLISH
      );
      setTitles(
        (lookupTitles || []).map((t: any) => ({
          value: (t.lookupId ??
            t.LookupId ??
            t.id ??
            t.Id ??
            t.code ??
            t.Code) as any,
          label: t.nameEN || t.NameEN || t.name || t.Name,
        }))
      );

      toast.success("New title added successfully");
      closeModal();
    } catch (e: any) {
      const friendly =
        "An error occurred while adding the new title. Please try again.";
      toast.error(e.message ?? friendly);
    } finally {
      setIsModalLoading(false);
    }
  };

  return (
    <>
      {isRegister ? (
        <MarketingLayout>
          <div className="mx-auto max-w-7xl px-3 sm:px-4 lg:px-6 xl:px-8 py-4 sm:py-6 lg:py-8 xl:py-10 w-full">
            <div className="flex justify-center mb-6 gap-2">
              <div
                className={`h-2 w-16 rounded-full ${currentStep === 1 ? "bg-primary-600" : "bg-gray-200"}`}
              />
              <div
                className={`h-2 w-16 rounded-full ${currentStep === 2 ? "bg-primary-600" : "bg-gray-200"}`}
              /> 
              {!isDemoPackage(form.packageId) && (
                <div
                  className={`h-2 w-16 rounded-full ${currentStep === 3 ? "bg-primary-600" : "bg-gray-200"}`}
                />
              )}
              <div
                className={`h-2 w-16 rounded-full ${currentStep === 4 ? "bg-primary-600" : "bg-gray-200"}`}
              />
            </div>
            {currentStep === 1 && (
              <div className="animate-in fade-in slide-in-from-right-4 duration-300">
                {!isView && !isProfile && !isEdit && (
                  <Card>
                    <CardHeader title="User Authentication" />
                    <CardBody>
                      <Grid cols={2} gap="lg" responsive={true}>
                        <GridItem>
                          <Input
                            label="Name"
                            name="name"
                            value={authForm.name}
                            onChange={(e) =>
                              setAuthForm({ ...authForm, name: e.target.value })
                            }
                            placeholder="Enter your name"
                            fullWidth
                            error={errors.name}
                            required
                            disabled={isView}
                          />
                        </GridItem>
                        <GridItem>
                          <Input
                            label="Email"
                            name="email"
                            type="email"
                            value={authForm.email}
                            onChange={(e) => {
                              const newEmail = e.target.value;
                              setAuthForm({...authForm, email: newEmail, username: newEmail});
                              // If email changes and user is not verified, reset verification state
                              if (isCodeRequired && !isVerified) {
                                setIsVerified(false);
                                setVerificationCode("");
                                setShowSuccess(false);
                              }
                            }}
                            placeholder="Enter your email"
                            fullWidth
                            error={errors.email}
                            required
                            disabled={isView || (alreadyAuth && isCodeRequired === false)}
                          />
                        </GridItem>
                        <GridItem colSpan={2}>
                          <div className="flex flex-col sm:flex-row items-start gap-3">
                            <div className="w-full sm:w-44 flex-shrink-0">
                              <Select
                                label="Country Code"
                                options={countries}
                                value={authForm.phoneCountryId}
                                onChange={(e) =>
                                  setAuthForm({
                                    ...authForm,
                                    phoneCountryId: e.target.value,
                                  })
                                }
                                fullWidth
                                error={errors.phoneCountryId}
                                required
                                disabled={isView}
                              />
                            </div>
                            <div className="flex-1 w-full">
                              <Input
                                label="Mobile Number"
                                name="phoneNumber"
                                type="tel"
                                inputMode="numeric"
                                pattern="\\d*"
                                value={authForm.phoneNumber}
                                onChange={(e) => setAuthForm({...authForm, phoneNumber: e.target.value})}
                                fullWidth
                                error={errors.phoneNumber}
                                required
                                disabled={isView}
                              />
                            </div>
                          </div>
                        </GridItem>
                        {!alreadyAuth && (
                          <>  
                        <GridItem>
                          <Input
                            label="Password"
                            name="password"
                            type="password"
                            value={authForm.password}
                            onChange={(e) => setAuthForm({...authForm, password: e.target.value})}
                            placeholder="Enter your password"
                            fullWidth
                            error={errors.password}
                            required
                            disabled={isView || isVerified}
                          />
                        </GridItem>

                        <GridItem>
                          <Input
                            label="Confirm Password"
                            name="confirmPassword"
                            type="password"
                            value={authForm.confirmPassword}
                            onChange={(e) => setAuthForm({...authForm, confirmPassword: e.target.value})}
                            placeholder="Re-enter your password"
                            fullWidth
                            error={errors.confirmPassword}
                            required
                            disabled={isView || isVerified}
                          />
                        </GridItem>
                        </>
                      )}
                      </Grid>
                    </CardBody>
                  </Card>
                )}
              </div>
            )}
            {currentStep === 2 && (
              <div className="animate-in fade-in slide-in-from-right-4 duration-300">
                  <Card>
            <CardHeader title={showSuccess ? "" : "Verify Email"} className="text-primary-600 font-bold text-xl sm:text-2xl"/>
            <CardBody>
              {!showSuccess ? (
                <div className="flex flex-col items-center gap-10">
                  <p className="text-center text-gray-600">
                    We've sent a code to your email. Please enter it below.
                  </p>
                  <Input 
                    label=""
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value)}
                    placeholder="X-X-X-X-X-X"
                    fullWidth
                    className="text-center"
                  />
                  <div className="flex flex-col lg:flex-row gap-3 w-full lg:col-span-2 sm:col-span-1">
                  <Button onClick={handleResendCode} variant="secondary" fullWidth>
                    Resend Code
                  </Button>
                  <Button onClick={handleVerifyCode} variant="primary" fullWidth>
                    Confirm Code
                  </Button>
                </div>
                </div>
              ) : (
                <div className="text-center p-6">
                  <CheckCircleIcon
                    sx={{
                      fontSize: 64,
                      color: "success.main",
                      mb: 2,
                    }}
                  />
                  <h3>Account Verified!</h3>
                  <p>You can now log in to your dashboard.</p>
                </div>
              )}
            </CardBody>
          </Card>
              </div>
            )} 
            {currentStep === 3 && !isDemoPackage(form.packageId) && (
              <div className="animate-in fade-in slide-in-from-right-4 duration-300">
                <Card>
                  <CardHeader title="Payment" />
                  <CardBody>
                    <div className="flex flex-col items-center justify-center py-8 space-y-6">
                      <div className="text-center">
                        <h3 className="text-xl font-semibold text-gray-900 mb-2">
                          Complete Your Registration
                        </h3>
                        <p className="text-gray-600">
                          Please proceed with payment to complete your registration.
                        </p>
                      </div>
                      <div className="bg-primary-50 border border-primary-200 rounded-lg p-6 w-full max-w-md">
                        <div className="flex items-center justify-between mb-4">
                          <span className="text-gray-700 font-medium">Amount:</span>
                          <span className="text-2xl font-bold text-primary-600">
                            {!form.packageId ? "$0.00" : 
                             allPackages.length === 0 ? "Loading..." :
                             packagePrice > 0 ? `$${packagePrice.toFixed(2)}${(form.isYearly ?? false) ? " /yr" : " /mo"}` : "$0.00"}
                          </span>
                        </div>
                      </div>
                      <Button
                        variant="primary"
                        size="lg"
                        onClick={onPay}
                        className="w-full max-w-md"
                      >
                        Pay Now
                      </Button>
                    </div>
                  </CardBody>
                </Card>
              </div>
            )}
            {/* Step 4: Company Info */}
            {currentStep === 4 && (
              <div className="animate-in fade-in slide-in-from-right-4 duration-300">
                <Card className="overflow-hidden w-full">
                  {!isProfile && (
                    <div className="p-1 sm:p-2 lg:p-3 rounded-xl">
                      <div className="flex justify-center gap-3">
                        <div className="py-2 text-base sm:text-xl font-bold">
                          <h1>Register as</h1>
                        </div>
                        <div className="mb-0 inline-flex justify-center items-center rounded-full border border-secondary-300 shadow-md bg-white p-1">
                          <button
                            onClick={() => handleFormChange(true)}
                            className={`px-4 py-2 rounded-full text-m font-Large ${
                              company
                                ? "bg-primary-600 text-white"
                                : "text-secondary-800"
                            }`}
                            disabled={isIndividual}
                          >
                            Company
                          </button>
                          <button
                            onClick={() => handleFormChange(false)}
                            className={`px-4 py-2 rounded-full text-m font-Large ${
                              !company
                                ? "bg-primary-600 text-white"
                                : "text-secondary-800"
                            }`}
                            disabled={!isIndividual}
                          >
                            Personal
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                  <CardBody className="p-3 sm:p-4 md:p-6 overflow-x-hidden w-full">
                    <div
                      className={`grid grid-cols-1 gap-4 sm:gap-6 w-full ${
                        showLivePreview ? "lg:grid-cols-2" : ""
                      }`}
                    >
                      <div className="flex col-span-1 lg:col-span-2 order-1 w-full justify-center items-center mb-4">
                        <div className="mb-3 sm:mb-4">
                          <h3 className="text-base sm:text-lg font-semibold text-gray-800">
                            {company
                              ? `Company Information`
                              : `Personal Information`}
                          </h3>
                        </div>
                      </div>
                      <div
                        className={`${
                          showLivePreview ? "lg:col-span-1" : ""
                        } order-2 w-full`}
                      >
                        <div className="flex flex-col items-center space-y-4 sm:space-y-6 w-full">
                          <div className="flex flex-col items-center space-y-3 sm:space-y-4 w-full">
                            <ImageEditor
                              currentImage={form.logo}
                              onImageChange={handleImageChange}
                              className="max-w-full sm:max-w-md w-full"
                            />
                            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 w-full">
                              {company && (
                                <Checkbox
                                  label="Show Logo"
                                  name="showLogo"
                                  checked={company ? form.showLogo : false}
                                  onChange={handleChange}
                                  disabled={isView}
                                />
                              )}
                              <Checkbox
                                label="Live Preview"
                                name="showLivePreview"
                                checked={showLivePreview}
                                onChange={(e) =>
                                  setShowLivePreview(e.target.checked)
                                }
                                disabled={isView}
                              />
                            </div>
                          </div>
                          {renderLogoAssistant()}

                          {showLivePreview && (
                            <div className="mt-4 sm:mt-6 w-full hidden lg:block">
                              <Card>
                                <CardHeader title="Live Preview" />
                                <CardBody className="p-3 sm:p-6">
                                  <div className="flex justify-center">
                                    <div className="scale-90 origin-top w-full max-w-sm">
                                      <EmployeeProfile
                                        employee={getDummyEmployee()}
                                        variant="preview"
                                        defaultSectionsOpen={true}
                                      />
                                    </div>
                                  </div>
                                </CardBody>
                              </Card>
                            </div>
                          )}
                        </div>
                      </div>
                      <div
                        className={`${
                          showLivePreview ? "lg:col-span-1" : ""
                        } order-3 lg:order-3 w-full min-w-0`}
                      >
                        <Grid
                          cols={2}
                          gap="lg"
                          responsive={true}
                          className="w-full"
                        >
                          {company ? (
                            <>
                              <GridItem>
                                <Input
                                  label="Name"
                                  name="name"
                                  value={form.name}
                                  onChange={handleChange}
                                  fullWidth
                                  error={errors.name}
                                  required
                                  disabled={isView}
                                />
                                {logoInsights?.companyName &&
                                  logoInsights.companyName.trim() &&
                                  logoInsights.companyName
                                    .trim()
                                    .toLowerCase() !==
                                    (form.name || "").trim().toLowerCase() && (
                                    <div className="flex items-center justify-between mt-1">
                                      <span className="text-xs text-secondary-500">
                                        AI detected:
                                        <span className="ml-1 font-semibold text-secondary-900">
                                          {logoInsights.companyName}
                                        </span>
                                      </span>
                                      <button
                                        type="button"
                                        onClick={applySuggestedName}
                                        className="text-xs font-semibold text-primary-600 hover:text-primary-700"
                                      >
                                        Use name
                                      </button>
                                    </div>
                                  )}
                              </GridItem>
                              <GridItem>
                                <Input
                                  label="Address"
                                  name="address"
                                  value={form.address}
                                  onChange={handleChange}
                                  fullWidth
                                  error={errors.address}
                                  required
                                  disabled={isView}
                                />
                              </GridItem>

                              <GridItem colSpan={2}>
                                <div className="flex flex-col sm:flex-row items-start gap-3">
                                  <div className="w-full sm:w-44 flex-shrink-0">
                                    <Select
                                      label="Country Code"
                                      options={countries}
                                      value={form.phoneCountryId}
                                      onChange={handleCountryChange}
                                      fullWidth
                                      error={errors.phoneCountryId}
                                      required
                                      disabled={isView}
                                    />
                                  </div>
                                  <div className="flex-1 w-full">
                                    <Input
                                      label="Mobile Number"
                                      name="phoneNumber"
                                      type="tel"
                                      inputMode="numeric"
                                      pattern="\\d*"
                                      value={form.phoneNumber}
                                      onChange={handleMobileChange}
                                      fullWidth
                                      error={errors.phoneNumber}
                                      required
                                      disabled={isView}
                                    />
                                  </div>
                                </div>
                              </GridItem>

                              {!form.isFreelancer && (isAdd || isRegister) && (
                                <>
                                  <GridItem>
                                    <Input
                                      label="Domain"
                                      name="domain"
                                      placeholder="@company.com"
                                      value={form.domain}
                                      onChange={handleChange}
                                      fullWidth
                                      error={errors.domain}
                                      required
                                      disabled={isView}
                                    />
                                  </GridItem>
                                  <GridItem>
                                    <Input
                                      label="Confirm Domain"
                                      name="confirmDomain"
                                      placeholder="@company.com"
                                      value={form.confirmDomain}
                                      onChange={handleChange}
                                      fullWidth
                                      error={errors.confirmDomain}
                                      required
                                      disabled={isView}
                                    />
                                  </GridItem>
                                </>
                              )}
                              {(isEdit || isView) && (
                                <GridItem>
                                  <Input
                                    label="Domain"
                                    name="domain"
                                    placeholder="@company.com"
                                    value={form.domain}
                                    onChange={handleChange}
                                    fullWidth
                                    error={errors.domain}
                                    required={false}
                                    disabled={true}
                                  />
                                </GridItem>
                              )}

                              <GridItem>
                                <Input
                                  label="Website"
                                  name="website"
                                  value={form.website}
                                  onChange={handleChange}
                                  fullWidth
                                  error={errors.website}
                                  required
                                  disabled={isView}
                                />
                              </GridItem>
                            </>
                          ) : (
                            <>
                              <GridItem>
                                <Input
                                  label="First Name"
                                  name="firstName"
                                  value={personalForm.firstName}
                                  onChange={handleChange}
                                  fullWidth
                                  error={errors.firstName}
                                  required
                                  disabled={isView}
                                />
                              </GridItem>
                              <GridItem>
                                <Input
                                  label="Last Name"
                                  name="lastName"
                                  value={personalForm.lastName}
                                  onChange={handleChange}
                                  fullWidth
                                  error={errors.lastName}
                                  required
                                  disabled={isView}
                                />
                              </GridItem>
                              <GridItem>
                                <Input
                                  label="Middle Name"
                                  name="middleName"
                                  value={personalForm.middleName}
                                  onChange={handleChange}
                                  fullWidth
                                  error={errors.middleName}
                                  disabled={isView}
                                />
                              </GridItem>
                              <GridItem>
                                <div className="flex items-end gap-2">
                                  <div className="flex-1">
                                    <Select
                                      label="Title"
                                      name="titleId"
                                      options={titles}
                                      value={personalForm.titleId}
                                      onChange={handleSelectChange}
                                      fullWidth
                                      placeholder="Select a title"
                                      disabled={isView}
                                      required
                                      error={errors.titleId}
                                    />
                                  </div>
                                  {/* {!isView && (
                                <Button
                                  type="button"
                                  variant="outline"
                                  onClick={openModal}
                                  className="px-3 py-2 h-10"
                                  title="Add new title"
                                >
                                  +
                                </Button>
                              )} */}
                                </div>
                              </GridItem>
                              <GridItem colSpan={2}>
                                <div className="flex items-start gap-3">
                                  <div className="w-44">
                                    <Select
                                      label="Country Code"
                                      options={countries}
                                      value={personalForm.mobileCountryId}
                                      onChange={handleCountryChange}
                                      fullWidth
                                      error={errors.mobileCountryId}
                                      required
                                      disabled={isView}
                                    />
                                  </div>
                                  <div className="flex-1">
                                    <Input
                                      label="Mobile Number"
                                      name="mobileNumber"
                                      type="tel"
                                      inputMode="numeric"
                                      pattern="\\d*"
                                      value={personalForm.mobileNumber}
                                      onChange={handleMobileChange}
                                      fullWidth
                                      error={errors.mobileNumber}
                                      required
                                      disabled={isView}
                                    />
                                  </div>
                                </div>
                              </GridItem>
                              <GridItem>
                                <Input
                                  label="Email"
                                  name="personalEmail"
                                  type="email"
                                  value={personalForm.personalEmail}
                                  onChange={handleChange}
                                  fullWidth
                                  error={errors.personalEmail}
                                  required
                                  disabled={isView || isEdit}
                                />
                              </GridItem>

                              <GridItem>
                                <Input
                                  label="Website"
                                  name="website"
                                  value={form.website}
                                  onChange={handleChange}
                                  fullWidth
                                  error={errors.website}
                                  disabled={isView}
                                />
                              </GridItem>
                            </>
                          )}
                          {!isProfile && (
                            <GridItem>
                              <Select
                                label="Package"
                                options={company?packages:indvPackages}
                                value={form.packageId}
                                onChange={handlePackageChange}
                                fullWidth
                                error={errors.packageId}
                                required
                                disabled={id ? true : (isRegister && (currentStep === 4 || paymentCompleted || savedCompanyId)) ? true : false}
                                placeholder="Select a package"
                              />
                            </GridItem>
                          )}

                          <GridItem>
                            <ColorPicker
                              label="Primary Color"
                              value={form.primaryColor || "#01878f"}
                              onChange={handleColorChange("primaryColor")}
                              showPresets={true}
                              disabled={isView}
                              presetColors={palettePresets}
                            />
                          </GridItem>
                          <GridItem>
                            <ColorPicker
                              label="Secondary Color"
                              value={form.secondaryColor || "#e5e7eb"}
                              onChange={handleColorChange("secondaryColor")}
                              showPresets={true}
                              disabled={isView}
                              presetColors={palettePresets}
                            />
                          </GridItem>
                          <GridItem>
                            <ColorPicker
                              label="Font Color"
                              value={form.fontColor || "#000"}
                              onChange={handleColorChange("fontColor")}
                              showPresets={true}
                              disabled={isView}
                              presetColors={palettePresets}
                            />
                          </GridItem>
                          <GridItem>
                            <Input
                              label="LinkedIn URL"
                              name="linkedInUrl"
                              value={form.linkedInUrl}
                              onChange={handleChange}
                              fullWidth
                              error={errors.linkedInUrl}
                              disabled={isView}
                            />
                          </GridItem>
                          <GridItem>
                            <Input
                              label="Facebook URL"
                              name="facebookUrl"
                              value={form.facebookUrl}
                              onChange={handleChange}
                              fullWidth
                              error={errors.facebookUrl}
                              disabled={isView}
                            />
                          </GridItem>
                          <GridItem>
                            <Input
                              label="Instagram URL"
                              name="instagramUrl"
                              value={form.instagramUrl}
                              onChange={handleChange}
                              fullWidth
                              error={errors.instagramUrl}
                              disabled={isView}
                            />
                          </GridItem>
                          <GridItem>
                            <Input
                              label="X URL"
                              name="xUrl"
                              value={form.xUrl}
                              onChange={handleChange}
                              fullWidth
                              error={errors.xUrl}
                              disabled={isView}
                            />
                          </GridItem>
                          <GridItem colSpan={2}>
                            <Textarea
                              label="Summary"
                              name="summary"
                              value={form.summary}
                              onChange={handleChange}
                              fullWidth
                              error={errors.summary}
                              disabled={isView}
                              rows={4}
                              placeholder="Enter a brief description of your company..."
                            />
                          </GridItem>
                        </Grid>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </div>
            )}

            <div className="flex flex-col sm:flex-row justify-end gap-3 mt-4 sm:mt-6">
              {/* Hide Previous on payment (step 3) and company/individual (step 4) */}
              {(currentStep !== 3 && currentStep !== 4) && (
                <Button
                  variant="outline"
                  onClick={() =>
                    currentStep === 1 ? navigate(ROUTES.LOGIN) : setCurrentStep(currentStep - 1)
                  }
                  disabled={isLoading}
                  className="w-full sm:w-auto"
                >
                  {currentStep === 1 ? "Back to Login" : "Previous"}
                </Button>
              )}
              {currentStep < totalSteps ? (
                <Button variant="primary" onClick={handleNext} disabled={(currentStep === 2 && !isVerified) || isLoading}>
                  Next Step
                </Button>
              ) : (
                <Button
                  variant="primary"
                  onClick={onSubmit}
                  isLoading={isLoading}
                >
                  Register
                </Button>
              )}
            </div>
          </div>
          <Modal
            open={isModalOpen}
            title="Add New Title"
            onCancel={closeModal}
            onConfirm={saveNewLookup}
            confirmText="Save"
            cancelText="Cancel"
            isProcessing={isModalLoading}
          >
            <div className="space-y-4">
              <Input
                label="Title (English)"
                name="valueEn"
                value={modalForm.valueEn}
                onChange={handleModalChange}
                fullWidth
                error={modalErrors.valueEn}
                required
                placeholder="Enter title in English"
              />
            </div>
          </Modal>
        </MarketingLayout>
      ) : (
        <MainLayout>
          <Card>
            <CardHeader
              title={
                isProfile
                  ? "Company Profile"
                  : isEdit
                    ? "Edit Company"
                    : isView
                      ? "View Company"
                      : "New Company"
              }
            />
            <CardBody>
              {!isProfile && (
                <div
                  className="mb-8 p-6 rounded-xl border shadow-sm"
                  style={{
                    background: `linear-gradient(to right, ${form.primaryColor}15, ${form.primaryColor}25)`,
                    borderColor: `${form.primaryColor}40`,
                  }}
                >
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      <div
                        className="w-10 h-10 rounded-full flex items-center justify-center"
                        style={{ backgroundColor: `${form.primaryColor}20` }}
                      >
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          style={{ color: form.primaryColor }}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                          />
                        </svg>
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <Checkbox
                          label=""
                          name="isFreelancer"
                          checked={form.isFreelancer}
                          onChange={handleChange}
                          disabled={isView || isEdit}
                        />
                        <h3 className="text-lg font-semibold text-gray-900">
                          Individual Account
                        </h3>
                      </div>
                      <p className="text-sm text-gray-600 leading-relaxed">
                        Select this option if you are an independent freelancer
                        or consultant. This will customize your profile display
                        and may affect how clients discover your services.
                      </p>
                      {form.isFreelancer && (
                        <div
                          className="mt-3 p-3 rounded-lg border"
                          style={{
                            backgroundColor: `${form.primaryColor}20`,
                            borderColor: `${form.primaryColor}40`,
                          }}
                        >
                          <p
                            className="text-xs font-medium"
                            style={{ color: form.primaryColor }}
                          >
                            ✓ Individual mode enabled - Your profile will be
                            optimized for individual service providers
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <div
                className={`grid grid-cols-1 gap-4 sm:gap-6 ${
                  showLivePreview ? "lg:grid-cols-2" : ""
                }`}
              >
                <div
                  className={`${
                    showLivePreview ? "lg:col-span-1" : ""
                  } order-2 lg:order-1`}
                >
                  <div className="flex flex-col items-center space-y-4 sm:space-y-6">
                    <div className="flex flex-col items-center space-y-3 sm:space-y-4 w-full">
                      <ImageEditor
                        currentImage={form.logo}
                        onImageChange={handleImageChange}
                        className="max-w-full sm:max-w-md w-full"
                      />
                      <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 w-full">
                        <Checkbox
                          label="Show Logo"
                          name="showLogo"
                          checked={form.showLogo}
                          onChange={handleChange}
                          disabled={isView}
                        />
                        <Checkbox
                          label="Live Preview"
                          name="showLivePreview"
                          checked={showLivePreview}
                          onChange={(e) => setShowLivePreview(e.target.checked)}
                          disabled={isView}
                        />
                      </div>
                      {renderLogoAssistant()}
                    </div>

                    {showLivePreview && (
                      <div className="mt-4 sm:mt-6 w-full hidden lg:block">
                        <Card>
                          <CardHeader title="Live Preview" />
                          <CardBody className="p-3 sm:p-6">
                            <div className="flex justify-center">
                              <div className="scale-90 origin-top w-full max-w-sm">
                                <EmployeeProfile
                                  employee={getDummyEmployee()}
                                  variant="preview"
                                  defaultSectionsOpen={true}
                                />
                              </div>
                            </div>
                          </CardBody>
                        </Card>
                      </div>
                    )}
                  </div>
                </div>
                <div
                  className={`${
                    showLivePreview ? "lg:col-span-1" : ""
                  } order-1 lg:order-2`}
                >
                  <div className="mb-3 sm:mb-4">
                    <h3 className="text-base sm:text-lg font-semibold text-gray-800">
                      Company Information
                    </h3>
                  </div>
                  <Grid cols={2} gap="lg" responsive={true}>
                    <GridItem>
                      <Input
                        label="Name"
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                        fullWidth
                        error={errors.name}
                        required
                        disabled={isView}
                      />
                      {logoInsights?.companyName &&
                        logoInsights.companyName.trim() &&
                        logoInsights.companyName.trim().toLowerCase() !==
                          (form.name || "").trim().toLowerCase() && (
                          <div className="flex items-center justify-between mt-1">
                            <span className="text-xs text-secondary-500">
                              AI detected:
                              <span className="ml-1 font-semibold text-secondary-900">
                                {logoInsights.companyName}
                              </span>
                            </span>
                            <button
                              type="button"
                              onClick={applySuggestedName}
                              className="text-xs font-semibold text-primary-600 hover:text-primary-700"
                            >
                              Use name
                            </button>
                          </div>
                        )}
                    </GridItem>
                    <GridItem>
                      <Input
                        label="Address"
                        name="address"
                        value={form.address}
                        onChange={handleChange}
                        fullWidth
                        error={errors.address}
                        required
                        disabled={isView}
                      />
                    </GridItem>

                    <GridItem colSpan={2}>
                      <div className="flex flex-col sm:flex-row items-start gap-3">
                        <div className="w-full sm:w-44 flex-shrink-0">
                          <Select
                            label="Country Code"
                            options={countries}
                            value={form.phoneCountryId}
                            onChange={handleCountryChange}
                            fullWidth
                            error={errors.phoneCountryId}
                            required
                            disabled={isView}
                          />
                        </div>
                        <div className="flex-1 w-full">
                          <Input
                            label="Mobile Number"
                            name="phoneNumber"
                            type="tel"
                            inputMode="numeric"
                            pattern="\\d*"
                            value={form.phoneNumber}
                            onChange={handleMobileChange}
                            fullWidth
                            error={errors.phoneNumber}
                            required
                            disabled={isView}
                          />
                        </div>
                      </div>
                    </GridItem>
                    {!form.isFreelancer && isAdd && (
                      <>
                        <GridItem>
                          <Input
                            label="Domain"
                            name="domain"
                            placeholder="@company.com"
                            value={form.domain}
                            onChange={handleChange}
                            fullWidth
                            error={errors.domain}
                            required
                            disabled={isView}
                          />
                        </GridItem>
                        <GridItem>
                          <Input
                            label="Confirm Domain"
                            name="confirmDomain"
                            placeholder="@company.com"
                            value={form.confirmDomain}
                            onChange={handleChange}
                            fullWidth
                            error={errors.confirmDomain}
                            required
                            disabled={isView}
                          />
                        </GridItem>
                      </>
                    )}
                    {(isEdit || isView) && (
                      <GridItem>
                        <Input
                          label="Domain"
                          name="domain"
                          placeholder="@company.com"
                          value={form.domain}
                          onChange={handleChange}
                          fullWidth
                          error={errors.domain}
                          required={false}
                          disabled={true}
                        />
                      </GridItem>
                    )}

                    <GridItem>
                      <Input
                        label="Website"
                        name="website"
                        value={form.website}
                        onChange={handleChange}
                        fullWidth
                        error={errors.website}
                        required
                        disabled={isView}
                      />
                    </GridItem>
                    {!isProfile && (
                      <GridItem>
                        <Select
                          label="Package"
                          options={company?packages:indvPackages}
                          value={form.packageId}
                          onChange={handlePackageChange}
                          fullWidth
                          error={errors.packageId}
                          required
                          disabled={true}
                          placeholder="Select a package"
                        />
                      </GridItem>
                    )}
                    <GridItem>
                      <ColorPicker
                        label="Primary Color"
                        value={form.primaryColor || "#01878f"}
                        onChange={handleColorChange("primaryColor")}
                        showPresets={true}
                        disabled={isView}
                        presetColors={palettePresets}
                      />
                    </GridItem>
                    <GridItem>
                      <ColorPicker
                        label="Secondary Color"
                        value={form.secondaryColor || "#e5e7eb"}
                        onChange={handleColorChange("secondaryColor")}
                        showPresets={true}
                        disabled={isView}
                        presetColors={palettePresets}
                      />
                    </GridItem>
                    <GridItem>
                      <ColorPicker
                        label="Font Color"
                        value={form.fontColor || "#000"}
                        onChange={handleColorChange("fontColor")}
                        showPresets={true}
                        disabled={isView}
                        presetColors={palettePresets}
                      />
                    </GridItem>
                    <GridItem>
                      <Input
                        label="LinkedIn URL"
                        name="linkedInUrl"
                        value={form.linkedInUrl}
                        onChange={handleChange}
                        fullWidth
                        error={errors.linkedInUrl}
                        disabled={isView}
                      />
                    </GridItem>
                    <GridItem>
                      <Input
                        label="Facebook URL"
                        name="facebookUrl"
                        value={form.facebookUrl}
                        onChange={handleChange}
                        fullWidth
                        error={errors.facebookUrl}
                        disabled={isView}
                      />
                    </GridItem>
                    <GridItem>
                      <Input
                        label="Instagram URL"
                        name="instagramUrl"
                        value={form.instagramUrl}
                        onChange={handleChange}
                        fullWidth
                        error={errors.instagramUrl}
                        disabled={isView}
                      />
                    </GridItem>
                    <GridItem>
                      <Input
                        label="X URL"
                        name="xUrl"
                        value={form.xUrl}
                        onChange={handleChange}
                        fullWidth
                        error={errors.xUrl}
                        disabled={isView}
                      />
                    </GridItem>
                    <GridItem colSpan={2}>
                      <Textarea
                        label="Summary"
                        name="summary"
                        value={form.summary}
                        onChange={handleChange}
                        fullWidth
                        error={errors.summary}
                        disabled={isView}
                        rows={4}
                        placeholder="Enter a brief description of your company..."
                      />
                    </GridItem>
                  </Grid>
                </div>
              </div>
            </CardBody>
          </Card>
          {!isView && !isProfile && !isEdit && (
            <Card>
              <CardHeader title="Authentication" />
              <CardBody>
                <Grid cols={2} gap="lg" responsive={true}>
                  <GridItem>
                    <Input
                      label="Name"
                      name="name"
                      value={authForm.name}
                      onChange={handleChange}
                      placeholder="Enter your name"
                      fullWidth
                      error={errors.name}
                      required
                      disabled={isView}
                    />
                  </GridItem>
                  <GridItem>
                    <Input
                      label="Username"
                      name="username"
                      value={authForm.username}
                      onChange={handleChange}
                      placeholder="Enter your username"
                      fullWidth
                      error={errors.username}
                      required
                      disabled={isView}
                    />
                  </GridItem>
                  <GridItem colSpan={2}>
                    <div className="flex flex-col sm:flex-row items-start gap-3">
                      <div className="w-full sm:w-44 flex-shrink-0">
                        <Select
                          label="Country Code"
                          options={countries}
                          value={authForm.phoneCountryId}
                          onChange={handleCountryChange}
                          fullWidth
                          error={errors.phoneCountryId}
                          required
                          disabled={isView}
                        />
                      </div>
                      <div className="flex-1 w-full">
                        <Input
                          label="Mobile Number"
                          name="phoneNumber"
                          type="tel"
                          inputMode="numeric"
                          pattern="\\d*"
                          value={authForm.phoneNumber}
                          onChange={handleMobileChange}
                          fullWidth
                          error={errors.phoneNumber}
                          required
                          disabled={isView}
                        />
                      </div>
                    </div>
                  </GridItem>
                  <GridItem>
                    <Input
                      label="Email"
                      name="email"
                      type="email"
                      value={authForm.email}
                      onChange={handleChange}
                      placeholder="Enter your email"
                      fullWidth
                      error={errors.email}
                      required
                      disabled={isView}
                    />
                  </GridItem>
                  <GridItem>
                    <Input
                      label="Password"
                      name="password"
                      type="password"
                      value={authForm.password}
                      onChange={handleChange}
                      placeholder="Enter your password"
                      fullWidth
                      error={errors.password}
                      required
                      disabled={isView}
                    />
                  </GridItem>
                  <GridItem>
                    <Input
                      label="Confirm Password"
                      name="confirmPassword"
                      type="password"
                      value={authForm.confirmPassword}
                      onChange={handleChange}
                      placeholder="Re-enter your password"
                      fullWidth
                      error={errors.confirmPassword}
                      required
                      disabled={isView}
                    />
                  </GridItem>
                </Grid>
              </CardBody>
            </Card>
          )}
          <div className="flex flex-col sm:flex-row justify-end gap-3 mt-4 sm:mt-6">
            <Button
              variant="outline"
              onClick={() =>
                navigate(isRegister ? ROUTES.LOGIN : ROUTES.COMPANIES)
              }
              disabled={isLoading}
              className="w-full sm:w-auto"
            >
              Back
            </Button>
            {!isView && (
              <Button
                variant="primary"
                onClick={onSubmit}
                isLoading={isLoading}
                disabled={isLoading}
                className="w-full sm:w-auto"
              >
                {isRegister ? "Register" : "Save"}
              </Button>
            )}
          </div>
        </MainLayout>
      )}
    </>
  );
};
